import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AllowedDevice, DeviceFirmware } from '@app/core/interfaces/allowed-device';
import { AllowedDeviceResellerUpdate } from '@app/core/interfaces/allowed-device-reseller-update';
import { AssignedAllowedDevice } from '@app/core/interfaces/assigned-allowed-device';
import { PhoneRedirectionInfo } from '@app/core/interfaces/phone-redirection-info';
import { UserLevel } from '@app/core/models/user-level';
import { Utils } from '@app/shared/services/utils.service';
import { throwPortalError } from '@app/utils/error/portal-error';
import { wrapError } from '@app/utils/rxjs';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { EnhancedAllowedDevice } from '../interfaces/enhanced-allowed-device';

@Injectable({
  providedIn: 'root',
})
export class AllowedDeviceRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  assignDevice(deviceAssignment: AllowedDeviceResellerUpdate) {
    return this.utils.apiUrl('/allowedDeviceAssignments').pipe(
      mergeMap((url) => this.http.post<AssignedAllowedDevice>(url, deviceAssignment)),
      wrapError('allowed-device-repository:assign-device')
    );
  }

  create(deviceFirmware: DeviceFirmware): Observable<AllowedDevice> {
    return this.utils.routeBasedApiUrl('/deviceFirmwares').pipe(
      mergeMap((url) => this.http.post<AllowedDevice>(url, deviceFirmware)),
      wrapError('allowed-device-repository:create')
    );
  }

  delete(id: number): Observable<void> {
    return this.utils.routeBasedApiUrl(`/allowedDevices/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('allowed-device-repository:delete-by-id')
    );
  }

  deleteAssignedDevice(id: number): Observable<void> {
    return this.utils.apiUrl(`/allowedDeviceAssignments/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('allowed-device-repository:delete-assigned-device')
    );
  }

  /**
   * Get a list of assigned allowed devices mapped to EnhancedAllowedDevice[]
   * This must not be used for system level
   */
  list(): Observable<EnhancedAllowedDevice[]> {
    return this.utils.routeBasedApiUrl('/allowedDevices').pipe(
      mergeMap((url) => this.http.get<EnhancedAllowedDevice[]>(url)),
      wrapError('allowed-device-repository:list')
    );
  }

  listAssignedDevices(): Observable<AssignedAllowedDevice[]> {
    return this.utils
      .apiUrl('/allowedDeviceAssignments')
      .pipe(mergeMap((url) => this.http.get<AssignedAllowedDevice[]>(url)));
  }

  listPhoneRedirectionInfo(): Observable<PhoneRedirectionInfo[]> {
    return this.utils
      .apiUrl(`/phoneRedirectionInfo`)
      .pipe(mergeMap((url) => this.http.get<PhoneRedirectionInfo[]>(url)));
  }

  listSystemAllowedDevices(): Observable<AllowedDevice[]> {
    return this.utils.routeBasedApiUrl('/allowedDevices', UserLevel.SystemAdmin).pipe(
      mergeMap((url) => this.http.get<AllowedDevice[]>(url)),
      wrapError('allowed-device-repository:list')
    );
  }

  setDefault(id: number): Observable<void> {
    return this.utils.apiUrl(`/allowedDevices/${id}/default`).pipe(
      mergeMap((url) => this.http.put<void>(url, undefined)),
      wrapError('allowed-device-repository:set-as-default')
    );
  }

  update(deviceFirmware: DeviceFirmware): Observable<AllowedDevice> {
    if (deviceFirmware.id == null) {
      return throwPortalError({
        cause: new Error('Allowed device has no id'),
        id: 'allowed-device-repository:update',
      });
    }

    return this.utils.routeBasedApiUrl(`/deviceFirmwares/${deviceFirmware.id}`).pipe(
      mergeMap((url) => this.http.put<AllowedDevice>(url, deviceFirmware)),
      wrapError('allowed-device-repository:update')
    );
  }

  updateAssignedDevice(id: number, deviceAssignment: AllowedDeviceResellerUpdate) {
    return this.utils.apiUrl(`/allowedDeviceAssignments/${id}`).pipe(
      mergeMap((url) => this.http.put<AssignedAllowedDevice>(url, deviceAssignment)),
      wrapError('allowed-device-repository:update-assigned-device')
    );
  }

  listDeviceFirmware(): Observable<DeviceFirmware[]> {
    return this.utils.routeBasedApiUrl('/allowedDevices').pipe(
      mergeMap((url) => this.http.get<DeviceFirmware[]>(url)),
      wrapError('allowed-device-repository:list')
    );
  }
}
